import { useEffect, useState } from 'react';
import { Box, Typography, useTheme, Divider, Button, Card, CardContent } from '@mui/material';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import { useParams, Link } from 'react-router-dom';
import { can } from '../../utils/permissions';

function getReportGuide2(data, guideId) {
  const guides = Array.isArray(data?.reportGuides) ? data.reportGuides : [];
  const g = guides.find((x) => String(x?.guideId) === String(guideId));
  return g?.reportGuide2 ?? null;
}

function mileageDisplay(m) {
  if (!m) return null;
  return {
    garage: m.mileageGarage ?? m.startingPoint ?? '',
    departure: m.mileageDeparturePoint ?? m.departurePoint ?? '',
    final: m.mileageFinal ?? m.dropoffPoint ?? '',
    invoice: m.mileageInvoice ?? m.netMileage ?? '',
  };
}

const buildCollectionDisplayRows = (items = []) =>
  (Array.isArray(items) ? items : []).map((item, idx) => {
    const excursion =
      typeof item?.excursion === 'string' && item.excursion.trim().length
        ? item.excursion.trim()
        : String(item?.excursion ?? '');
    const passengers = item?.passengers === '' || item?.passengers == null ? '' : Number(item.passengers || 0);
    const totalCollection =
      item?.total_collection === '' || item?.total_collection == null ? '' : Number(item.total_collection || 0);
    return {
      no: String(item?.no ?? idx + 1),
      excursion,
      passengers,
      total_collection: totalCollection,
    };
  });

const TripReportView = () => {
  const { countryId, departureDate, guideId } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');

  const adminUserJson = localStorage.getItem('adminUser');
  const adminUser = adminUserJson ? JSON.parse(adminUserJson) : null;
  const isCoordinator = Number(adminUser?.role) === 3;

  const [data, setData] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [totalPassengers, setTotalPassengers] = useState(0);
  const [collections, setCollections] = useState(null);
  const [guideName, setGuideName] = useState('');
  const handleDownloadGuide2 = () => {
    try {
      const lines = [];
      // Title
      lines.push('"REPORT GUIDE 2"');
      if (resolvedGuideName) {
        lines.push(
          ['Guide Name', resolvedGuideName].map((value) => '"' + String(value).replace(/"/g, '""') + '"').join(',')
        );
      }
      lines.push('');
      // Collections table header
      lines.push(
        ['No.', 'Excursion', 'Passengers', 'Total Collection (€)']
          .map((h) => '"' + h + '"')
          .join(',')
      );
      const items = collections?.items || [];
      const totalAmountCollected = Number(collections?.totalAmountCollected || 0);
      const averageSales = Number(collections?.averageSalesPerPassenger || 0);
      const displayRows = buildCollectionDisplayRows(items);
      displayRows.forEach((displayRow) => {
        const passengersValue =
          displayRow.passengers === '' || displayRow.passengers == null ? '' : String(displayRow.passengers);
        const hasTotal = displayRow.total_collection !== '' && displayRow.total_collection != null;
        const totalValue = hasTotal ? Number(displayRow.total_collection).toFixed(2) : '';
        const csvRow = [displayRow.no, displayRow?.excursion ?? '', passengersValue, totalValue].map(
          (v) => '"' + String(v).replace(/"/g, '""') + '"'
        );
        lines.push(csvRow.join(','));
      });
      lines.push('');
      // Totals summary
      lines.push(
        ['', '', 'Total amount', Number(collections?.totalFullPriceAmount || 0).toFixed(2)]
          .map((v) => '"' + v + '"')
          .join(',')
      );
      lines.push(
        ['', '', 'Gratis', '-' + Number(collections?.totalGratisAmount || 0).toFixed(2)]
          .map((v) => '"' + v + '"')
          .join(',')
      );
      lines.push(
        ['', '', 'Discount', '-' + Number(collections?.manualDiscountTotal || 0).toFixed(2)]
          .map((v) => '"' + v + '"')
          .join(',')
      );
      lines.push(
        ['', '', '1. Collected amount', totalAmountCollected.toFixed(2)]
          .map((v) => '"' + v + '"')
          .join(',')
      );
      lines.push(
        ['', '', 'Average sales per passenger (€)', averageSales.toFixed(2)]
          .map((v) => '"' + v + '"')
          .join(',')
      );
      lines.push(
        [
          '',
          '',
          '2. Guide fee (' + String(collections?.guideFeePercentage || 0) + '%)',
          '-' + Number(collections?.guideFee || 0).toFixed(2),
        ]
          .map((v) => '"' + v + '"')
          .join(',')
      );
      lines.push(
        ['', '', 'To be settled (€)', Number(collections?.netToSettle || 0).toFixed(2)]
          .map((v) => '"' + v + '"')
          .join(',')
      );
      lines.push('');
      // Sections
      const pushSection = (title, rows) => {
        lines.push('"' + title + '"');
        if (!rows || rows.length === 0) {
          lines.push('"No items."');
          lines.push('');
          return;
        }
        lines.push(['Name', 'Date', 'Amount'].map((v) => '"' + v + '"').join(','));
        rows.forEach((r) => {
          const dt = r.entryDate != null && r.entryDate !== '' ? String(r.entryDate).slice(0, 10) : '';
          lines.push(
            [
              '"' + String(r.name || '').replace(/"/g, '""') + '"',
              '"' + dt.replace(/"/g, '""') + '"',
              '"' + Number(r.amount || 0).toFixed(2) + '"',
            ].join(',')
          );
        });
        lines.push('');
      };
      pushSection('Additional income', data?.sections?.income || []);
      pushSection('Destination-related costs', data?.sections?.excursion_expense || []);
      pushSection('Other income', data?.sections?.travel_expense || []);
      pushSection('Other expenses', data?.sections?.guide_expense || []);
      if (data?.netSettlementAmount != null) {
        lines.push(
          ['', '', 'Net Settlement Amount (€)', Number(data.netSettlementAmount).toFixed(2)]
            .map((v) => '"' + v + '"')
            .join(',')
        );
        lines.push('');
      }
      const mv = mileageDisplay(data?.mileage);
      lines.push('"Mileage:"');
      if (mv && (mv.garage !== '' || mv.departure !== '' || mv.final !== '' || mv.invoice !== '')) {
        lines.push('"1. Initial mileage from garage:","' + String(mv.garage) + '"');
        lines.push('"2. Initial mileage from departure point:","' + String(mv.departure) + '"');
        lines.push('"3. Final mileage (after passengers exit):","' + String(mv.final) + '"');
        lines.push('"4. Mileage for invoice (= 3 − 1):","' + String(mv.invoice) + '"');
      } else {
        lines.push('"No mileage recorded."');
      }
      const rg2 = getReportGuide2(data, guideId);
      const exM = rg2?.sections?.excursion_mileage || [];
      lines.push('');
      lines.push('"Excursion mileage:"');
      if (exM.length) {
        lines.push(['Excursion name', 'Distance traveled', 'Date'].map((v) => '"' + v + '"').join(','));
        exM.forEach((r) => {
          lines.push(
            [
              '"' + String(r.excursionName || '').replace(/"/g, '""') + '"',
              '"' + String(r.distanceTraveled ?? '') + '"',
              '"' + String(r.entryDate || '').replace(/"/g, '""') + '"',
            ].join(',')
          );
        });
      } else {
        lines.push('"No excursion mileage recorded."');
      }
      lines.push('');
      lines.push('"Trip description"');
      lines.push(
        '"' +
        String(rg2?.sections?.trip_description || '')
          .replace(/"/g, '""')
          .replace(/\r?\n/g, ' ') +
        '"'
      );
      lines.push('');
      lines.push('"Guide allowance total"');
      lines.push(
        '"' + String(Number(rg2?.guideAllowanceTotal ?? rg2?.sections?.guide_allowance_total ?? 0).toFixed(2)) + '"'
      );
      // Build and download
      const csvContent = lines.join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeDate = (departureDate || '').replace(/[^0-9-]/g, '');
      link.download = `report_guide2_${countryId || 'country'}_${safeDate || 'date'}_${guideId || 'guide'}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export Report Guide 2 CSV', err);
    }
  };

  const fetchDetails = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/${countryId}/${departureDate}?guide_id=${guideId}`,
      { headers: { token } }
    );
    const json = await res.json();
    if (json.success) {
      setData(json.data);
      const guides = Array.isArray(json.data?.reportGuides) ? json.data.reportGuides : [];
      const matchedGuide = guides.find((g) => String(g?.guideId) === String(guideId));
      const extractedName = matchedGuide?.guideName;
      if (extractedName && String(extractedName).trim()) {
        setGuideName(String(extractedName).trim());
      }
    }
  };

  const fetchBreakdown = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/${countryId}/${departureDate}/${guideId}/excursion-hotel-breakdown`,
      { headers: { token } }
    );
    const json = await res.json();
    if (json.success) {
      setBreakdown(json.data || []);
      setTotalPassengers(json.totalPassengers || 0);
    }
  };

  const fetchCollections = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/${countryId}/${departureDate}/${guideId}/collections-summary`,
        { headers: { token } }
      );
      const json = await res.json();
      if (json.success) {
        setCollections(json.data);
      } else {
        console.error('Error fetching collections:', json.message);
        setCollections({ items: [], totalAmountCollected: 0, guideFeePercentage: 0, guideFee: 0, netToSettle: 0 });
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
      setCollections({ items: [], totalAmountCollected: 0, guideFeePercentage: 0, guideFee: 0, netToSettle: 0 });
    }
  };

  useEffect(() => {
    fetchDetails();
    fetchBreakdown();
    fetchCollections();
    // Fallback: fetch guide name from country names list
    (async () => {
      try {
        if (!countryId || !guideId) return;
        const res = await fetch(`${process.env.REACT_APP_APILINK}/api/admin/names/${countryId}`, {
          headers: { token },
        });
        const json = await res.json();
        const list = json?.data || json?.names || [];
        const match = Array.isArray(list)
          ? list.find((g) => String(g?.id || g?.GuideID || g?.guideId) === String(guideId))
          : null;
        const name = (
          match?.name ||
          match?.GuideName ||
          [match?.firstName, match?.surname || match?.lastName].filter(Boolean).join(' ') ||
          ''
        ).trim();
        if (name) setGuideName(name);
      } catch (_) { }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId, departureDate, guideId]);

  const sum = (rows = []) => rows.reduce((a, r) => a + Number(r.amount || 0), 0);

  const resolvedGuideName =
    guideName ||
    data?.guideName ||
    data?.guide?.name ||
    data?.guide?.fullName ||
    [data?.guide?.firstName, data?.guide?.lastName].filter(Boolean).join(' ').trim() ||
    '';

  const collectionDisplayRows = buildCollectionDisplayRows(collections?.items || []);
  const rg2 = data ? getReportGuide2(data, guideId) : null;
  const mv = data ? mileageDisplay(data.mileage) : null;

  return (
    <Box m="20px">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Header title="VIEW TRIP REPORT" subtitle={`${countryId} • ${departureDate}`} />
        {isCoordinator ? (
          <Button
            component={Link}
            to="/guide"
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 20px',
              '&:hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            Go Back
          </Button>
        ) : (
          <Button
            component={Link}
            to="/trip-reports"
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 20px',
              '&:hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            Go Back
          </Button>
        )}
      </Box>
      {!data ? (
        <Typography>Loading...</Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={3}>
          <Box mt={4} p={2} sx={{ background: colors.primary[500], borderRadius: 0, boxShadow: 'none', mb: 1 }}>
            <Box
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              }}
              gap={2}
            >
              <Summary title="Additional income" value={sum(data.sections.income)} />
              <Summary title="Destination-related costs" value={sum(data.sections.excursion_expense)} />
              <Summary title="Other income" value={sum(data.sections.travel_expense)} />
              <Summary title="Other expenses" value={sum(data.sections.guide_expense)} />
            </Box>
          </Box>

          <Divider />
          <Card sx={{ backgroundColor: colors.primary[400], borderRadius: 2 }}>
            <CardContent>
              {breakdown.length ? (
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="h5" fontWeight="bold">
                      REPORT GUIDE 1
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        try {
                          const lines = [];
                          lines.push('"REPORT GUIDE 1"');
                          if (resolvedGuideName) {
                            lines.push(
                              ['Guide Name', resolvedGuideName]
                                .map((value) => '"' + String(value).replace(/"/g, '""') + '"')
                                .join(',')
                            );
                          }
                          lines.push('');
                          lines.push('"Total number of passengers: ' + String(totalPassengers) + '"');
                          lines.push('');
                          const headers = ['No.', 'Excursion', 'Hotel', 'Number of passengers'];
                          lines.push(headers.map((h) => '"' + h + '"').join(','));
                          (breakdown || []).forEach((row, idx) => {
                            const csvRow = [
                              String(idx + 1),
                              row?.excursion ?? '',
                              row?.hotel ?? '',
                              String(row?.num_passengers ?? 0),
                            ].map((v) => '"' + String(v).replace(/"/g, '""') + '"');
                            lines.push(csvRow.join(','));
                          });
                          const csvContent = lines.join('\n');
                          const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          const safeDate = (departureDate || '').replace(/[^0-9-]/g, '');
                          link.download = `report_guide1_${countryId || 'country'}_${safeDate || 'date'}_${guideId || 'guide'
                            }.csv`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(url);
                        } catch (err) {
                          console.error('Failed to export Report Guide 1 CSV', err);
                        }
                      }}
                      sx={{
                        minWidth: 130,
                        px: 2.5,
                        py: 1.25,
                        fontSize: '0.95rem',
                        backgroundColor: colors.blueAccent[700],
                        color: colors.grey[100],
                        textTransform: 'none',
                        fontWeight: 'bold',
                        '&:hover': { backgroundColor: colors.blueAccent[800] },
                      }}
                    >
                      Download Report guide 1
                    </Button>
                  </Box>
                  {resolvedGuideName && (
                    <Typography variant="h5" sx={{ mb: 1 }}>
                      {resolvedGuideName}
                    </Typography>
                  )}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="h5">Total number of passengers: {totalPassengers}</Typography>
                  </Box>
                  <Box display="grid" gridTemplateColumns="60px 2fr 2fr 1fr" gap={1} mb={1}>
                    <Typography variant="h4" fontWeight="bold" color="#4cceac">
                      No.
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="#4cceac">
                      Excursion
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="#4cceac">
                      Hotel
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="#4cceac">
                      Number of passengers
                    </Typography>
                  </Box>
                  {breakdown.map((row, idx) => (
                    <Box key={idx} display="grid" gridTemplateColumns="60px 2fr 2fr 1fr" gap={1} mb={0.5}>
                      <Typography variant="body1">{idx + 1}</Typography>
                      <Typography variant="body1">{row.excursion}</Typography>
                      <Typography variant="body1">{row.hotel}</Typography>
                      <Typography variant="body1">{row.num_passengers}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="h5" fontWeight="bold">
                      REPORT GUIDE 1
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        try {
                          const lines = [];
                          lines.push('"REPORT GUIDE 1"');
                          if (resolvedGuideName) {
                            lines.push(
                              ['Guide Name', resolvedGuideName]
                                .map((value) => '"' + String(value).replace(/"/g, '""') + '"')
                                .join(',')
                            );
                          }
                          lines.push('');
                          lines.push('"Total number of passengers: ' + String(totalPassengers) + '"');
                          lines.push('');
                          const headers = ['No.', 'Excursion', 'Hotel', 'Number of passengers'];
                          lines.push(headers.map((h) => '"' + h + '"').join(','));
                          (breakdown || []).forEach((row, idx) => {
                            const csvRow = [
                              String(idx + 1),
                              row?.excursion ?? '',
                              row?.hotel ?? '',
                              String(row?.num_passengers ?? 0),
                            ].map((v) => '"' + String(v).replace(/"/g, '""') + '"');
                            lines.push(csvRow.join(','));
                          });
                          const csvContent = lines.join('\n');
                          const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          const safeDate = (departureDate || '').replace(/[^0-9-]/g, '');
                          link.download = `report_guide1_${countryId || 'country'}_${safeDate || 'date'}_${guideId || 'guide'
                            }.csv`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(url);
                        } catch (err) {
                          console.error('Failed to export Report Guide 1 CSV', err);
                        }
                      }}
                      sx={{
                        minWidth: 140,
                        px: 2.5,
                        py: 1.25,
                        fontSize: '0.95rem',
                        backgroundColor: colors.blueAccent[700],
                        color: colors.grey[100],
                        textTransform: 'none',
                        fontWeight: 'bold',
                        '&:hover': { backgroundColor: colors.blueAccent[800] },
                      }}
                    >
                      Download Report guide 1
                    </Button>
                  </Box>
                  {resolvedGuideName && (
                    <Typography variant="h5" sx={{ mb: 1 }}>
                      {resolvedGuideName}
                    </Typography>
                  )}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="h5">Total number of passengers: {totalPassengers}</Typography>
                  </Box>
                  <Typography variant="body1">No breakdown data.</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          <Divider />
          <Card sx={{ backgroundColor: colors.primary[400], borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h5" fontWeight="bold">
                  REPORT GUIDE 2
                </Typography>
                {can('reports.trip.export') && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleDownloadGuide2}
                    sx={{
                      minWidth: 130,
                      px: 2.5,
                      py: 1.25,
                      fontSize: '0.95rem',
                      backgroundColor: colors.blueAccent[700],
                      color: colors.grey[100],
                      textTransform: 'none',
                      fontWeight: 'bold',
                      '&:hover': { backgroundColor: colors.blueAccent[800] },
                    }}
                  >
                    Download Report guide 2
                  </Button>
                )}
              </Box>
              {collections ? (
                <>
                  {resolvedGuideName && (
                    <Typography variant="h5" sx={{ mb: 1 }}>
                      {resolvedGuideName}
                    </Typography>
                  )}
                  <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={1} mb={1}>
                    <Box display="grid" gridTemplateColumns="60px 2fr 1fr" gap={1} gridColumn="span 8">
                      <Typography variant="h4" fontWeight="bold" color="#4cceac">
                        No.
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="#4cceac">
                        Excursion
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="#4cceac">
                        Passengers
                      </Typography>
                    </Box>
                    <Typography variant="h4" fontWeight="bold" color="#4cceac" gridColumn="span 4">
                      Total Collection
                    </Typography>
                  </Box>
                  {collectionDisplayRows.length > 0 ? (
                    <>
                      {collectionDisplayRows.map((item) => {
                        const hasTotal = item.total_collection !== '' && item.total_collection != null;
                        return (
                          <Box key={item.no} display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={1} mb={0.5}>
                            <Box display="grid" gridTemplateColumns="60px 2fr 1fr" gap={1} gridColumn="span 8">
                              <Typography variant="body1">{item.no}</Typography>
                              <Typography variant="body1">{item.excursion}</Typography>
                              <Typography variant="body1">
                                {item.passengers === '' || item.passengers == null ? '' : item.passengers}
                              </Typography>
                            </Box>
                            <Typography variant="body1" gridColumn="span 4">
                              {hasTotal ? `€ ${Number(item.total_collection).toFixed(2)}` : ''}
                            </Typography>
                          </Box>
                        );
                      })}
                      <Divider sx={{ my: 2 }} />
                      <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
                        <Typography variant="body1">Total amount</Typography>
                        <Typography variant="body1">€ {(collections.totalFullPriceAmount || 0).toFixed(2)}</Typography>
                      </Box>
                      <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
                        <Typography variant="body1">Gratis</Typography>
                        <Typography variant="body1">- € {(collections.totalGratisAmount || 0).toFixed(2)}</Typography>
                      </Box>
                      <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
                        <Typography variant="body1">Discount</Typography>
                        <Typography variant="body1">- € {(collections.manualDiscountTotal || 0).toFixed(2)}</Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
                        <Typography variant="h6" fontWeight="bold">1. Collected amount</Typography>
                        <Typography variant="h6" fontWeight="bold">€ {(collections.totalAmountCollected || 0).toFixed(2)}</Typography>
                      </Box>
                      <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
                        <Typography variant="body1">2. Guide fee ({collections.guideFeePercentage}%)</Typography>
                        <Typography variant="body1">- € {(collections.guideFee || 0).toFixed(2)}</Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
                        <Typography variant="h6" fontWeight="bold">3. To settle (excursions) (= 1 − 2)</Typography>
                        <Typography variant="h6" fontWeight="bold">€ {(collections.netToSettle || 0).toFixed(2)}</Typography>
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body1">No collections data.</Typography>
                  )}
                </>
              ) : (
                <Typography variant="body1">Loading collections data...</Typography>
              )}
            </CardContent>
          </Card>

          <Divider />
          <Card sx={{ backgroundColor: colors.primary[400], borderRadius: 2 }}>
            <CardContent>
              <SectionList title="Additional income" rows={data.sections.income} showDate />
              <Divider sx={{ my: 2 }} />
              <SectionList title="Destination-related costs" rows={data.sections.excursion_expense} showDate />
              <Divider sx={{ my: 2 }} />
              <SectionList title="Other income" rows={data.sections.travel_expense} showDate />
              <Divider sx={{ my: 2 }} />
              <SectionList title="Other expenses" rows={data.sections.guide_expense} showDate />
              <Divider sx={{ my: 2 }} />
              {data.netSettlementAmount != null && (
                <Box
                  display="grid"
                  gridTemplateColumns="2fr 1fr"
                  gap={1}
                  mb={1}
                  px={1}
                  py={1.5}
                  sx={{ backgroundColor: colors.blueAccent[700], borderRadius: 1 }}
                >
                  <Typography variant="h5" fontWeight="bold">Net Settlement Amount</Typography>
                  <Typography variant="h5" fontWeight="bold" textAlign="right">
                    € {Number(data.netSettlementAmount).toFixed(2)}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="h4" fontWeight="bold" color="#4cceac">
                Mileage
              </Typography>
              {mv && (mv.garage !== '' || mv.departure !== '' || mv.final !== '' || mv.invoice !== '') ? (
                <Box mt={1}>
                  <Typography variant="body1">1. Initial mileage from garage: {mv.garage}</Typography>
                  <Typography variant="body1">2. Initial mileage from departure point: {mv.departure}</Typography>
                  <Typography variant="body1">3. Final mileage (after passengers exit): {mv.final}</Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    4. Mileage for invoice (= 3 − 1): {mv.invoice}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  No mileage recorded.
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="h4" fontWeight="bold" color="#4cceac">
                Excursion mileage
              </Typography>
              {rg2?.sections?.excursion_mileage?.length ? (
                <Box mt={1}>
                  <Box display="grid" gridTemplateColumns="2fr 1fr 1fr" gap={1} mb={1}>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#4cceac' }}>
                      Excursion name
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#4cceac' }}>
                      Distance traveled
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#4cceac' }}>
                      Date
                    </Typography>
                  </Box>
                  {rg2.sections.excursion_mileage.map((r) => (
                    <Box key={r.id} display="grid" gridTemplateColumns="2fr 1fr 1fr" gap={1} mb={0.5}>
                      <Typography variant="body1">{r.excursionName}</Typography>
                      <Typography variant="body1">{r.distanceTraveled}</Typography>
                      <Typography variant="body1">{r.entryDate || '—'}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  No excursion mileage recorded.
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="h4" fontWeight="bold" color="#4cceac">
                Trip description
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                {rg2?.sections?.trip_description?.trim() ? rg2.sections.trip_description : '—'}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h4" fontWeight="bold" color="#4cceac">
                Guide allowance total
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                € {Number(rg2?.guideAllowanceTotal ?? rg2?.sections?.guide_allowance_total ?? 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

const Summary = ({ title, value }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box width="100%" bgcolor={colors.primary[400]} p={2} borderRadius={2}>
      <Box display="flex" justifyContent="space-between">
        <Box>
          <Typography variant="h3" fontWeight="bold" sx={{ color: colors.grey[100] }}>
            € {Number(value || 0).toFixed(2)}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" mt="2px">
        <Typography variant="h4" sx={{ color: colors.greenAccent[500] }}>
          {title}
        </Typography>
      </Box>
    </Box>
  );
};

const SectionList = ({ title, rows = [], showDate = false }) => {
  const cols = showDate ? '2fr 1fr 1fr' : '2fr 1fr';
  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" color="#4cceac">
        {title}
      </Typography>
      {!rows.length ? (
        <Typography variant="body2">No items.</Typography>
      ) : (
        <Box>
          <Box display="grid" gridTemplateColumns={cols} gap={1} mt={1} mb={1}>
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#4cceac' }}>
              Name
            </Typography>
            {showDate ? (
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#4cceac' }}>
                Date
              </Typography>
            ) : null}
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#4cceac' }}>
              Amount
            </Typography>
          </Box>
          {rows.map((r) => (
            <Box key={r.id} display="grid" gridTemplateColumns={cols} gap={1} mb={0.5}>
              <Typography variant="body1">{r.name}</Typography>
              {showDate ? (
                <Typography variant="body1">
                  {r.entryDate != null && r.entryDate !== '' ? String(r.entryDate).slice(0, 10) : '—'}
                </Typography>
              ) : null}
              <Typography variant="body1">€ {Number(r.amount || 0).toFixed(2)}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TripReportView;
